import { applyWaitingUpdate } from "@/lib/pwa-update";
import { useCallback, useEffect, useRef, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

type PWAStatus = {
  isOfflineReady: boolean;
  isUpdateAvailable: boolean;
  isRegistered: boolean;
  applyUpdate: () => Promise<void>;
  isOnline: boolean;
};

export const usePWA = (): PWAStatus => {
  const [isRegistered, setIsRegistered] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const isApplyingUpdate = useRef(false);

  const {
    offlineReady: [offlineReady],
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    // The new worker took control. Every tab told about the update hears it,
    // not only the one that applied it: only that one reloads now. Another
    // tab may be streaming an answer or holding a draft — it reloads through
    // its own `applyUpdate` (route change, hidden tab, toast).
    onNeedReload() {
      if (isApplyingUpdate.current) window.location.reload();
    },
    onRegistered(registration) {
      setIsRegistered(true);
      console.log("PWA: Service Worker registered", registration);

      if (!registration) return;

      // Check for updates more frequently (every 5 minutes)
      const CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

      const checkForUpdates = async () => {
        if (!registration.installing && navigator.onLine) {
          try {
            await registration.update();
            console.log("PWA: Checked for updates");
          } catch (error) {
            console.error("PWA: Failed to check for updates:", error);
          }
        }
      };

      // Periodic check every 5 minutes
      const intervalId = setInterval(checkForUpdates, CHECK_INTERVAL);

      // Check for updates when window regains focus
      const handleFocus = () => {
        console.log("PWA: Window focused, checking for updates...");
        checkForUpdates();
      };

      // Check for updates when network comes back online
      const handleOnlineCheck = () => {
        console.log("PWA: Network online, checking for updates...");
        checkForUpdates();
      };

      window.addEventListener("focus", handleFocus);
      window.addEventListener("online", handleOnlineCheck);

      // Cleanup function
      return () => {
        clearInterval(intervalId);
        window.removeEventListener("focus", handleFocus);
        window.removeEventListener("online", handleOnlineCheck);
      };
    },
    onRegisterError(error) {
      console.error("PWA: Service Worker registration error", error);
    },
    onNeedRefresh() {
      console.log("PWA: New content available, update required!");
    },
    onOfflineReady() {
      console.log("PWA: App ready to work offline");
    },
  });

  const applyUpdate = useCallback(async () => {
    isApplyingUpdate.current = true;
    await applyWaitingUpdate({
      getRegistration: async () =>
        navigator.serviceWorker?.getRegistration(),
      activateWaitingWorker: () => updateServiceWorker(true),
      reload: () => window.location.reload(),
    });
  }, [updateServiceWorker]);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return {
    isOfflineReady: offlineReady,
    isUpdateAvailable: needRefresh,
    isRegistered,
    applyUpdate,
    isOnline,
  };
};
